import React, { useEffect, useState } from "react";
import { Button, toast } from "ui";
import ChoiceField from "../../../components/ChoiceField";
import { Conditions } from "./Conditions";
import InputField from "../../../components/InputField";
import Drawer from "../../../components/Drawer";
import { axiosApiInstance } from "../../../pages/api/axios";
import { parseErrorsToArray } from "../../../utils/parseErrors";
import type { RiskRuleFormFields, RiskRule } from "../types";
import { validateConditions, validateFormValues } from "../utils/validation";
import { useRiskManagementConditions } from "../utils/useRiskManagementConditions";
import { useMutation } from "@tanstack/react-query";

type EditRuleDrawerProps = {
  rule: RiskRule;
  refreshTable: () => void;
  showEditRule: boolean;
  setShowEditRule: React.Dispatch<React.SetStateAction<boolean>>;
};
export function EditRuleDrawer({
  rule,
  showEditRule,
  setShowEditRule,
  refreshTable,
}: EditRuleDrawerProps) {
  const [formFields, setFormFields] = useState<RiskRuleFormFields>({});

  useEffect(() => {
    setFormFields({
      name: rule?.name,
      risk_level: rule?.risk_level,
      reason: rule?.reason,
    });

    const mappedConditions = rule?.conditions?.map((condition) => {
      const { resource_type, ...resource_data } = condition;
      return {
        resource_type,
        resource_data,
      };
    });
    setConditions(mappedConditions ?? []);
  }, [rule]);

  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof RiskRuleFormFields, string>>
  >({});
  const [conditions, setConditions] = useState<Array<any>>([]);
  const [conditionsError, setConditionsError] = useState<string>("");

  function updateConditions(newConditions: Array<any>) {
    setConditions(newConditions);
  }

  function onFieldChange(fieldName: string, value: string) {
    setFormFields((prev) => ({ ...prev, [fieldName]: value }));
  }

  const editRuleMutation = useMutation({
    mutationFn: () =>
      axiosApiInstance.patch(`/d/risk_management/riskrules/${rule.id}/`, {
        id: rule.id,
        name: formFields["name"],
        risk_level: formFields["risk_level"],
        reason: formFields["reason"],
        conditions: conditions.map((condition) => {
          return {
            resource_type: condition.resource_type,
            ...condition?.resource_data,
          };
        }),
      }),
    onSuccess: () => {
      toast.success("Rule edited successfully");
      setShowEditRule(false);
      refreshTable();
    },
    onError: (error: any) => {
      const errors = parseErrorsToArray(error);
      toast.error(errors[0]);
    },
  });

  function editRule() {
    setFormErrors({});
    setConditionsError("");

    const formErrors = validateFormValues(formFields);
    if (Object.keys(formErrors ?? {}).length) {
      setFormErrors(formErrors);
      return;
    }

    const conditionsErrors = validateConditions(conditions);
    if (conditionsErrors.errorMessage) {
      setConditionsError(conditionsErrors.errorMessage);
      return;
    }

    editRuleMutation.mutate();
  }

  const { data: availableConditions } = useRiskManagementConditions({
    refetchOnMount: false,
  });

  return (
    <Drawer
      title="Edit Risk Rule"
      isOpen={showEditRule}
      onClose={() => setShowEditRule(false)}
      size="max-w-lg"
    >
      <div className="h-full overflow-y-auto">
        <div className="m-6 space-y-6">
          <div>
            <InputField
              className="space-y-0"
              field_name="name"
              label="Rule Name"
              value={formFields["name"] ?? ""}
              error_messages={formErrors}
              type="text"
              onChange={onFieldChange}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foundations-bold">
              Rule conditions
            </label>

            <Conditions
              availableConditions={availableConditions}
              conditions={conditions}
              updateConditions={updateConditions}
              error={conditionsError}
            />
          </div>

          <div>
            <ChoiceField
              field_name="risk_level"
              label="Risk Level"
              value={formFields["risk_level"] ?? ""}
              error_messages={formErrors}
              onChange={onFieldChange}
              choices={{
                MEDIUM_RISK: "Medium",
                HIGH_RISK: "High",
              }}
            />
          </div>

          <div>
            <InputField
              field_name="reason"
              label={
                <p>
                  Reason{" "}
                  <span className="text-foundations-subtle">(optional)</span>
                </p>
              }
              value={formFields["reason"] ?? ""}
              error_messages={formErrors}
              type="text"
              onChange={onFieldChange}
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 border-t border-t-foundations-subtle px-4 py-5 sm:px-6">
        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowEditRule(false)}
            className="ml-3"
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={editRuleMutation.isLoading}
            onClick={editRule}
            className="ml-3"
          >
            Save
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
